/** Equipment Detail (Demo Mode only) - nested detail screen for a single catalogue item,
 * opened by drilling into a row on the Equipment list (EquipmentCatalogue.tsx). Combines the
 * catalogue-level specification (what the equipment is, how it should be maintained) with a
 * "Site Usage" tab showing which sites currently have this equipment type installed - the
 * cross-site view that previously lived on the standalone Site Equipment page. No backend
 * catalogue or asset register exists yet - all data is static mock data, matching the same
 * "seeded illustrative numbers" approach used elsewhere in src/pages/EstateManagement.
 * lazy: detail fields (description, dimensions, service intervals, etc.) are illustrative
 * and shared across all catalogue items rather than modelled per-item; only name/category/
 * manufacturer/model/status vary by row. Fields are editable in local state only - Save
 * shows a confirmation snackbar rather than persisting, since there is no catalogue-item API
 * yet. Upgrade path: back with a real catalogue-item API response once the Equipment
 * backend exists. */

import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert, Box, Button, Chip, Grid, MenuItem, Snackbar, Stack, SvgIcon, Tab, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Tabs, TextField, Typography, useTheme,
} from '@mui/material';
import { Build, CheckCircle, Event, HighlightOff, Store } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import PageArea from '../../components/shared/PageArea';
import PageTitle from '../../components/shared/PageTitle';
import { Section } from '../../components/ui/Section';
import { MOCK_CATALOGUE } from './EquipmentCatalogue';

// Illustrative full specification, layered on top of whichever catalogue row was selected.
const EXAMPLE = {
  supplier: 'Nisbets Foodservice Equipment',
  description: 'Combination oven supporting steam, convection and combination cooking modes for high-volume production kitchens.',
  processCapabilities: 'Bake, Steam, Roast, Regenerate',
  energyType: 'Electric (3-phase)',
  dimensions: '1053 x 987 x 1085mm',
  capacity: '10 x 1/1 GN',
  power: '20.6 kW',
  water: 'Mains fed, softened',
  ventilation: 'Requires extract canopy',
  installation: 'Requires certified electrical & water connection',
  serviceInterval: 'Every 6 months',
  inspectionInterval: 'Annual (statutory)',
  lifespan: '10 years',
  warranty: '2 years parts & labour',
  complianceNotes: 'Annual statutory inspection (PSSR/gas safety where applicable), electrical safety (PAT/fixed wire) check aligned to the inspection interval above.',
  variants: ['iCombi Pro 6-1/1', 'iCombi Pro 10-1/1', 'iCombi Pro 20-1/1'],
};

// Physical assets installed at sites, keyed by the catalogue item they were sourced from.
const MOCK_SITE_USAGE = [
  { asset: 'TF-DWTN-KIT-0014', catalogueItem: 'Combi Oven', site: 'Downtown', zone: 'Main Kitchen', status: 'In Service', lastService: '2026-05-12', nextService: '2026-11-12' },
  { asset: 'TF-STMARY-KIT-0042', catalogueItem: 'Combi Oven', site: 'St Mary\u2019s School', zone: 'Main Kitchen', status: 'In Service', lastService: '2026-05-12', nextService: '2026-11-12' },
  { asset: 'TF-RVSD-STR-0003', catalogueItem: 'Walk-in Chiller', site: 'Riverside', zone: 'Chilled Store', status: 'In Service', lastService: '2026-04-30', nextService: '2026-10-30' },
  { asset: 'TF-UPTN-BAR-0022', catalogueItem: 'Espresso Machine', site: 'Uptown', zone: 'Coffee Bar', status: 'Under Repair', lastService: '2026-03-18', nextService: '2026-06-18' },
  { asset: 'TF-SGTE-KIT-0009', catalogueItem: 'Blast Freezer', site: 'Southgate', zone: 'Main Kitchen', status: 'Planned', lastService: '-', nextService: '-' },
];

const statusColour = (status: string) => {
  if (status === 'Under Repair' || status === 'Out of Service') return 'error';
  if (status === 'Planned') return 'default';
  return 'success';
};

interface EquipmentFormData {
  name: string;
  category: string;
  manufacturer: string;
  model: string;
  status: string;
  supplier: string;
  description: string;
  processCapabilities: string;
  energyType: string;
  dimensions: string;
  capacity: string;
  power: string;
  water: string;
  ventilation: string;
  installation: string;
  serviceInterval: string;
  inspectionInterval: string;
  lifespan: string;
  warranty: string;
  complianceNotes: string;
}

const EquipmentDetail = () => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const theme = useTheme();
  const { id } = useParams<{ id: string }>();
  const [tabValue, setTabValue] = useState('overview');
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);

  const catalogueItem = useMemo(
    () => MOCK_CATALOGUE.find((item) => item.name === decodeURIComponent(id ?? '')) ?? MOCK_CATALOGUE[0],
    [id],
  );

  const [formData, setFormData] = useState<EquipmentFormData>(() => ({
    name: catalogueItem.name,
    category: catalogueItem.category,
    manufacturer: catalogueItem.manufacturer,
    model: catalogueItem.model,
    status: catalogueItem.status,
    supplier: EXAMPLE.supplier,
    description: EXAMPLE.description,
    processCapabilities: catalogueItem.processCapabilities,
    energyType: EXAMPLE.energyType,
    dimensions: EXAMPLE.dimensions,
    capacity: EXAMPLE.capacity,
    power: EXAMPLE.power,
    water: EXAMPLE.water,
    ventilation: EXAMPLE.ventilation,
    installation: EXAMPLE.installation,
    serviceInterval: EXAMPLE.serviceInterval,
    inspectionInterval: EXAMPLE.inspectionInterval,
    lifespan: EXAMPLE.lifespan,
    warranty: EXAMPLE.warranty,
    complianceNotes: EXAMPLE.complianceNotes,
  }));

  const handleFieldChange = <K extends keyof EquipmentFormData>(field: K, value: EquipmentFormData[K]) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleCancel = () => {
    navigate('/estate-management/equipment');
  };

  const handleSave = () => {
    // lazy: no catalogue-item API exists yet, so Save only confirms locally rather than persisting.
    setShowSuccessMessage(true);
  };

  const siteUsage = useMemo(
    () => MOCK_SITE_USAGE.filter((row) => row.catalogueItem === catalogueItem.name),
    [catalogueItem],
  );

  const categories: { key: string; label: string; content: JSX.Element }[] = useMemo(() => [
    {
      key: 'overview',
      label: t('equipmentCatalogueTabOverview', 'Overview'),
      content: (
        <Grid container
          spacing={4}>
          <Grid item
            xs={12}
            md={6}>
            <TextField label={t('equipmentCatalogueName', 'Equipment Name')}
              fullWidth
              value={formData.name}
              onChange={(e) => handleFieldChange('name', e.target.value)} />
          </Grid>
          <Grid item
            xs={6}
            md={3}>
            <TextField label={t('estateManagementCategory', 'Category')}
              fullWidth
              value={formData.category}
              onChange={(e) => handleFieldChange('category', e.target.value)} />
          </Grid>
          <Grid item
            xs={6}
            md={3}>
            <TextField select
              label={t('estateManagementStatus', 'Status')}
              fullWidth
              value={formData.status}
              onChange={(e) => handleFieldChange('status', e.target.value)}>
              <MenuItem value="Approved">{t('equipmentStatusApproved', 'Approved')}</MenuItem>
              <MenuItem value="Deprecated">{t('equipmentStatusDeprecated', 'Deprecated')}</MenuItem>
            </TextField>
          </Grid>
          <Grid item
            xs={12}>
            <TextField label={t('equipmentCatalogueDescription', 'Description')}
              fullWidth
              multiline
              minRows={2}
              value={formData.description}
              onChange={(e) => handleFieldChange('description', e.target.value)} />
          </Grid>
          <Grid item
            xs={12}>
            <TextField label={t('equipmentCatalogueProcessCapabilities', 'Process Capabilities')}
              fullWidth
              helperText={t('equipmentCatalogueProcessCapabilitiesHint', 'Comma-separated list')}
              value={formData.processCapabilities}
              onChange={(e) => handleFieldChange('processCapabilities', e.target.value)} />
          </Grid>
        </Grid>
      ),
    },
    {
      key: 'manufacturerDetails',
      label: t('equipmentCatalogueTabManufacturer', 'Manufacturer Details'),
      content: (
        <Grid container
          spacing={4}>
          <Grid item
            xs={6}
            md={3}>
            <TextField label={t('equipmentManagementType', 'Manufacturer')}
              fullWidth
              value={formData.manufacturer}
              onChange={(e) => handleFieldChange('manufacturer', e.target.value)} />
          </Grid>
          <Grid item
            xs={6}
            md={3}>
            <TextField label={t('equipmentCatalogueModel', 'Model')}
              fullWidth
              value={formData.model}
              onChange={(e) => handleFieldChange('model', e.target.value)} />
          </Grid>
          <Grid item
            xs={6}
            md={3}>
            <TextField label={t('equipmentCatalogueSupplier', 'Supplier')}
              fullWidth
              value={formData.supplier}
              onChange={(e) => handleFieldChange('supplier', e.target.value)} />
          </Grid>
          <Grid item
            xs={6}
            md={3}>
            <TextField label={t('equipmentCatalogueWarranty', 'Warranty Information')}
              fullWidth
              value={formData.warranty}
              onChange={(e) => handleFieldChange('warranty', e.target.value)} />
          </Grid>
        </Grid>
      ),
    },
    {
      key: 'operationalUse',
      label: t('equipmentCatalogueTabOperationalUse', 'Operational Use'),
      content: (
        <Grid container
          spacing={4}>
          <Grid item
            xs={6}
            md={3}>
            <TextField label={t('equipmentCatalogueEnergyType', 'Energy Type')}
              fullWidth
              value={formData.energyType}
              onChange={(e) => handleFieldChange('energyType', e.target.value)} />
          </Grid>
          <Grid item
            xs={6}
            md={3}>
            <TextField label={t('equipmentCatalogueDimensions', 'Dimensions')}
              fullWidth
              value={formData.dimensions}
              onChange={(e) => handleFieldChange('dimensions', e.target.value)} />
          </Grid>
          <Grid item
            xs={6}
            md={3}>
            <TextField label={t('equipmentCatalogueCapacity', 'Capacity')}
              fullWidth
              value={formData.capacity}
              onChange={(e) => handleFieldChange('capacity', e.target.value)} />
          </Grid>
          <Grid item
            xs={6}
            md={3}>
            <TextField label={t('equipmentCataloguePower', 'Power Requirements')}
              fullWidth
              value={formData.power}
              onChange={(e) => handleFieldChange('power', e.target.value)} />
          </Grid>
          <Grid item
            xs={6}
            md={3}>
            <TextField label={t('equipmentCatalogueWater', 'Water Requirements')}
              fullWidth
              value={formData.water}
              onChange={(e) => handleFieldChange('water', e.target.value)} />
          </Grid>
          <Grid item
            xs={6}
            md={3}>
            <TextField label={t('equipmentCatalogueVentilation', 'Ventilation Requirements')}
              fullWidth
              value={formData.ventilation}
              onChange={(e) => handleFieldChange('ventilation', e.target.value)} />
          </Grid>
          <Grid item
            xs={6}
            md={3}>
            <TextField label={t('equipmentCatalogueInstallation', 'Installation Requirements')}
              fullWidth
              value={formData.installation}
              onChange={(e) => handleFieldChange('installation', e.target.value)} />
          </Grid>
        </Grid>
      ),
    },
    {
      key: 'siteUsage',
      label: t('equipmentCatalogueTabSiteUsage', 'Site Usage'),
      content: siteUsage.length === 0 ? (
        <Typography variant="textMD"
          color="text.secondary">
          {t('equipmentCatalogueSiteUsageEmpty', 'No sites currently report an installed asset for this catalogue item.')}
        </Typography>
      ) : (
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('equipmentManagementSite', 'Site')}</TableCell>
                <TableCell>{t('siteManagementTabZones', 'Zone')}</TableCell>
                <TableCell>{t('equipmentManagementAsset', 'Asset Reference')}</TableCell>
                <TableCell>{t('estateManagementStatus', 'Status')}</TableCell>
                <TableCell>{t('equipmentManagementLastService', 'Last Service')}</TableCell>
                <TableCell>{t('equipmentManagementNextService', 'Next Service')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {siteUsage.map((row) => (
                <TableRow key={row.asset}>
                  <TableCell>{row.site}</TableCell>
                  <TableCell>{row.zone}</TableCell>
                  <TableCell>{row.asset}</TableCell>
                  <TableCell>
                    <Chip label={row.status}
                      size="small"
                      color={statusColour(row.status)}
                      variant="outlined" />
                  </TableCell>
                  <TableCell>{row.lastService}</TableCell>
                  <TableCell>{row.nextService}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ),
    },
    {
      key: 'documentsManuals',
      label: t('equipmentCatalogueTabDocuments', 'Documents & Manuals'),
      content: (
        <Table>
          <TableBody>
            <TableRow>
              <TableCell>{t('equipmentCatalogueManual', 'Installation & Operation Manual.pdf')}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>{t('equipmentCatalogueCleaningInstructions', 'Cleaning Instructions.pdf')}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>{t('equipmentCatalogueSafetyInstructions', 'Safety Instructions.pdf')}</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      ),
    },
    {
      key: 'maintenanceRequirements',
      label: t('equipmentCatalogueTabMaintenance', 'Maintenance Requirements'),
      content: (
        <Grid container
          spacing={4}>
          <Grid item
            xs={6}
            md={4}>
            <TextField label={t('equipmentCatalogueServiceInterval', 'Service Interval')}
              fullWidth
              value={formData.serviceInterval}
              onChange={(e) => handleFieldChange('serviceInterval', e.target.value)} />
          </Grid>
          <Grid item
            xs={6}
            md={4}>
            <TextField label={t('equipmentCatalogueInspectionInterval', 'Inspection Interval')}
              fullWidth
              value={formData.inspectionInterval}
              onChange={(e) => handleFieldChange('inspectionInterval', e.target.value)} />
          </Grid>
          <Grid item
            xs={6}
            md={4}>
            <TextField label={t('equipmentCatalogueLifespan', 'Expected Lifespan')}
              fullWidth
              value={formData.lifespan}
              onChange={(e) => handleFieldChange('lifespan', e.target.value)} />
          </Grid>
        </Grid>
      ),
    },
    {
      key: 'complianceRequirements',
      label: t('equipmentCatalogueTabCompliance', 'Compliance Requirements'),
      content: (
        <Grid container
          spacing={4}>
          <Grid item
            xs={12}>
            <TextField label={t('equipmentCatalogueComplianceNotes', 'Compliance Notes')}
              fullWidth
              multiline
              minRows={3}
              value={formData.complianceNotes}
              onChange={(e) => handleFieldChange('complianceNotes', e.target.value)} />
          </Grid>
        </Grid>
      ),
    },
    {
      key: 'variants',
      label: t('equipmentCatalogueTabVariants', 'Variants'),
      content: (
        <Table>
          <TableBody>
            {EXAMPLE.variants.map((v) => (
              <TableRow key={v}>
                <TableCell>{v}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ),
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [t, formData, siteUsage]);

  const inServiceCount = siteUsage.filter((row) => row.status === 'In Service').length;
  const nextServiceDates = siteUsage.map((row) => row.nextService).filter((date) => date && date !== '-').sort();

  const METRIC_TILES = [
    { key: 'status', label: t('estateManagementStatus', 'Status'), value: formData.status === 'Approved' ? t('equipmentStatusApproved', 'Approved') : t('equipmentStatusDeprecated', 'Deprecated'), subtitle: t('equipmentDetailStatusSub', 'Catalogue approval status'), icon: formData.status === 'Approved' ? <CheckCircle /> : <HighlightOff />, color: formData.status === 'Approved' ? '#2e7d32' : '#d32f2f' },
    { key: 'sitesUsing', label: t('equipmentDetailSitesLabel', 'Sites using this item'), value: `${siteUsage.length}`, subtitle: t('equipmentDetailSitesSub', 'Installed across the estate'), icon: <Store />, color: '#1976d2' },
    { key: 'inService', label: t('equipmentDetailInService', 'Assets in service'), value: `${inServiceCount} / ${siteUsage.length}`, subtitle: t('equipmentDetailInServiceSub', 'Currently operational'), icon: <Build />, color: '#ed6c02' },
    { key: 'nextService', label: t('equipmentManagementNextService', 'Next Service'), value: nextServiceDates[0] ?? t('notSet', 'Not set'), subtitle: t('equipmentCatalogueServiceInterval', 'Service Interval') + `: ${formData.serviceInterval}`, icon: <Event />, color: '#9c27b0' },
  ];

  return (
    <PageArea>
      <Box display="flex"
        justifyContent="space-between"
        alignItems="flex-start"
        flexWrap="wrap"
        gap={2}
        sx={{ mb: 6 }}>
        <PageTitle subTitle={t('equipmentCatalogueTitle', 'Equipment')}>
          {formData.name}
        </PageTitle>
        <Stack direction="row"
          spacing={3}>
          <Button variant="secondary"
            onClick={handleCancel}>
            {t('cancel', 'Cancel')}
          </Button>
          <Button variant="primary"
            onClick={handleSave}>
            {t('save', 'Save')}
          </Button>
        </Stack>
      </Box>

      <Grid container
        spacing={4}
        sx={{ mb: 6 }}>
        {METRIC_TILES.map((tile) => (
          <Grid item
            xs={12}
            sm={6}
            md={3}
            key={tile.key}>
            <Section style={{ p: 4, mb: 0 }}>
              <Box display="flex"
                alignItems="center"
                justifyContent="space-between">
                <Box flex={1}>
                  <Box sx={{ color: theme.palette.custom.gray[600], fontSize: '10px', textTransform: 'uppercase', mb: 0.25, fontWeight: 500 }}>
                    {tile.label}
                  </Box>
                  <Box sx={{ fontSize: '20px', fontWeight: 600, color: theme.palette.custom.gray[900], mb: 0.25 }}>
                    {tile.value}
                  </Box>
                  <Box sx={{ fontSize: '12px', color: theme.palette.custom.gray[600] }}>
                    {tile.subtitle}
                  </Box>
                </Box>
                <Box sx={{ backgroundColor: `${tile.color}20`, borderRadius: '50%', width: 48, height: 48, display: 'flex', alignItems: 'center', justifyContent: 'center', color: tile.color }}>
                  <SvgIcon fontSize="medium">{tile.icon}</SvgIcon>
                </Box>
              </Box>
            </Section>
          </Grid>
        ))}
      </Grid>

      <Tabs
        value={tabValue}
        onChange={(_, value) => setTabValue(value)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ mb: '-1px', borderBottom: 1, borderColor: 'divider', ml: 4, mr: 4, px: 2 }}
      >
        {categories.map((category) => (
          <Tab key={category.key}
            value={category.key}
            label={category.label} />
        ))}
      </Tabs>

      <Section style={{ mt: 0, p: 5, pb: 0 }}>
        <Grid container
          spacing={8}>
          {categories.map((category) => (
            <Grid item
              xs={12}
              key={category.key}
              sx={{ display: tabValue === category.key ? 'block' : 'none' }}>
              <Box sx={{ pb: 5 }}>
                {category.content}
              </Box>
            </Grid>
          ))}
        </Grid>
      </Section>

      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        open={showSuccessMessage}
        autoHideDuration={4000}
        onClose={() => setShowSuccessMessage(false)}>
        <Alert onClose={() => setShowSuccessMessage(false)}
          severity="success"
          sx={{ width: '100%' }}>
          {t('equipmentDetailSaved', 'Equipment details saved.')}
        </Alert>
      </Snackbar>
    </PageArea>
  );
};

export default EquipmentDetail;
